/**
 * Weather Alarm · v2.7.30 (2026-02-21)
 * ------------------------------------
 * Standard NWS-style Emergency Alarm tone for Buller Taxi weather alerts.
 * Three-tone sequence (~853 / 960 / 1050 Hz) at ~1 s each, repeated 3×.
 * Uses Web Audio so it works on every connected dispatch, driver, and
 * rider device with zero install.
 *
 * Exposes:
 *   playWeatherAlarm()  — fires the 3-tone sequence ×3 (≈8 s total).
 *   stopWeatherAlarm()  — kills any in-flight playback immediately.
 *   isWeatherAlarmActive() — boolean
 */
import { isAudioLeader } from "@/lib/audioLeader";

let _ctx = null;
let _activeNodes = [];
let _active = false;

function _audioCtx() {
  if (_ctx) return _ctx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    _ctx = new C();
  } catch { /* noop */ }
  return _ctx;
}

export function isWeatherAlarmActive() {
  return _active;
}

export function stopWeatherAlarm() {
  for (const n of _activeNodes) {
    try { n.stop(); } catch { /* noop */ }
  }
  _activeNodes = [];
  _active = false;
}

/**
 * Fire the NWS Emergency Alert System "attention signal" — the
 * characteristic 3-tone burst broadcasters use before severe alerts.
 * Real EAS tones are 853 / 960 / 1050 Hz; this is a faithful Web-Audio
 * recreation (lower amplitude so it's not jarring inside a vehicle).
 */
export function playWeatherAlarm({ cycles = 3, gain = 0.18 } = {}) {
  // v2.7.65+E243 · Multi-window dedupe (Admin Console). Non-participant tabs
  // (driver/rider) never activate the election, so isAudioLeader() is true
  // for them and their weather alarm is unaffected.
  if (!isAudioLeader()) return;
  const ctx = _audioCtx();
  if (!ctx) return;
  // Some browsers suspend the context until user gesture; resume best-effort.
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch { /* noop */ }
  }
  stopWeatherAlarm();
  _active = true;
  const tones = [853, 960, 1050]; // EAS attention tones
  const dur = 1.0;  // seconds per tone
  let t = ctx.currentTime;
  for (let c = 0; c < cycles; c += 1) {
    for (const freq of tones) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t);
      // Soft attack/release so the alarm doesn't click on/off
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(gain, t + 0.05);
      g.gain.linearRampToValueAtTime(gain, t + dur - 0.05);
      g.gain.linearRampToValueAtTime(0, t + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur);
      _activeNodes.push(osc);
      t += dur;
    }
  }
  // Mark inactive after the sequence finishes
  const totalDur = cycles * tones.length * dur;
  setTimeout(() => {
    _active = false;
    _activeNodes = [];
  }, totalDur * 1000 + 200);
}
